import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react'
import { Row, Col } from 'antd'

import { TasksSection } from '../../../../tasks/components/index.ts'
import { InteractionsSection } from '../../../../interactions/components/index.ts'
import {
  DetailPanel,
  type DetailPanelItemType,
  type DetailPanelMode
} from '../../../../deals/components/forms/parts/DetailPanel.tsx'
import { FormSection } from '../../../../../core/components/FormSection.tsx'
import { InputManager } from '../../../../../core/components/InputManager/index.ts'
import { ProspectOverviewSection } from '../sections/ProspectOverviewSection.tsx'
import { InvestorSection } from '../sections/InvestorSection.tsx'
import { AgentDraftsSection } from '../../../../internal_agents/components/AgentDraftsSection.tsx'
import { AgentSignalsSection } from '../../../../internal_agents/components/AgentSignalsSection.tsx'
import { ProspectStatesStrip } from '../../../../internal_agents/components/ProspectStatesStrip.tsx'
import { ProspectBriefCard } from '../../../../internal_agents/components/ProspectBriefCard.tsx'
import { CompanyTeamTable } from '../../../../../core/components/CompanyTeamTable/index.tsx'

import { useSelector } from 'react-redux'
import { useAppDispatch } from '../../../../../state_manager/hooks/useAppDispatch'
import { createTask, updateTask, deleteTask as deleteTaskThunk } from '../../../../../store/slices/tasksSlice'
import {
  createInteraction,
  updateInteraction,
  deleteInteraction,
  fetchInteractionsForProspect,
  delinkInteraction
} from '../../../../interactions/services/interactionsService.ts'
import { updateProspectRecord } from '../../../../../state_manager/state_update_actions/fundraising/index.ts'
import { selectProspectById } from '../../../../../state_manager/state_update_actions/fundraising/selectors.ts'
import { updateActiveRecord } from '../../../../../state_manager/state_update_actions/active_records/active_record_actions.ts'
import { fetchFundraisingById } from '../../../services/fundraisingService.ts'
import {
  addInteraction,
  updateInteraction as updateInteractionAction,
  setInteractions,
  removeInteraction
} from '../../../../../state_manager/state_update_actions/interactions/interaction_actions.ts'
import { getAccessToken } from '../../../../../core/auth/authService'
import { store } from '../../../../../state_manager/store.ts'
import { ProspectFields, getInteractionSummaryFields } from '../../../utils/fieldConfigs.ts'
import { SECTION_INVESTOR_TEAM, SECTION_INTERACTION_SUMMARY } from '../../../../../state_manager/constants/strings.ts'
import { deriveProspectContacts, EMPTY_PROSPECT_CONTACTS } from '../../../utils/prospectContacts.ts'
import { TaskRecordFields } from '../../../../../state_manager/data_models/task_record.ts'
import { InteractionRecordFields } from '../../../../../state_manager/data_models/interaction_record.ts'

export function DashboardTab({
  prospectRecord,
  handleFieldChange,
  onPatchSuccess,
  recordId,
  companyTeamMembers: propCompanyTeamMembers,
  companyTeamMembersKey,
  onCompanyTeamMembersChange,
  onRefreshInteractionsReady,
  onRefreshTasksReady,
  onRefreshProspectStatesReady,
  onNavigateToAgentTab
}: {
  prospectRecord?: any
  handleFieldChange?: (field: string, value: any) => void
  onPatchSuccess?: (patchRecordId: string, updates: Record<string, any>) => void
  recordId?: string | null
  companyTeamMembers?: any[]
  companyTeamMembersKey?: string | undefined
  onCompanyTeamMembersChange?: (members: any[]) => void
  onRefreshInteractionsReady?: (refreshFn: () => Promise<void>) => void
  onRefreshTasksReady?: (refreshFn: () => Promise<void>) => void
  onRefreshProspectStatesReady?: (refreshFn: () => Promise<void>) => void
  onNavigateToAgentTab?: () => void
}) {
  const dispatch = useAppDispatch()
  const currentProspect = useSelector((state: any) =>
    recordId && recordId !== 'new' ? selectProspectById(state, recordId) : null
  )
  const [isTaskInEditMode, setIsTaskInEditMode] = useState(false)

  const [selectedContacts, setSelectedContacts] = useState(EMPTY_PROSPECT_CONTACTS)

  const companyTeamMembers = propCompanyTeamMembers || []
  const setCompanyTeamMembers =
    onCompanyTeamMembersChange || (() => console.warn('No companyTeamMembers change handler provided'))

  const prospect = prospectRecord || currentProspect

  useEffect(() => {
    if (prospect) setSelectedContacts(deriveProspectContacts(prospect))
  }, [prospect])

  const [selectedItem, setSelectedItem] = useState<any>(null)
  const [selectedItemType, setSelectedItemType] = useState<DetailPanelItemType | null>(null)
  const [detailMode, setDetailMode] = useState<DetailPanelMode>('view')

  const handleTaskSelect = useCallback((task: any) => {
    if (task === null) {
      setSelectedItem(null)
      setSelectedItemType(null)
      setDetailMode('view')
    } else {
      setSelectedItem(task)
      setSelectedItemType('task')
      setDetailMode('view')
    }
  }, [])

  const handleAddTask = useCallback(() => {
    setDetailMode('add-task')
    setSelectedItem(null)
    setSelectedItemType('task')
  }, [])

  const handleTaskEditModeChange = useCallback((isInEditMode: boolean) => {
    setIsTaskInEditMode(isInEditMode)
  }, [])

  const handleInteractionSelect = useCallback((interaction: any) => {
    setSelectedItem(interaction)
    setSelectedItemType('interaction')
    setDetailMode('view')
  }, [])

  const handleAddInteraction = useCallback(() => {
    setDetailMode('add-interaction')
    setSelectedItem(null)
    setSelectedItemType('interaction')
  }, [])

  const handleDetailClose = useCallback(() => {
    setSelectedItem(null)
    setSelectedItemType(null)
    setDetailMode('view')
  }, [])

  const handleDetailEdit = useCallback((item: any, itemType: string) => {
    if (itemType === 'task') {
      setDetailMode('edit-task')
    } else if (itemType === 'interaction') {
      setDetailMode('edit-interaction')
    }
  }, [])

  const handleDetailDelete = useCallback(
    async (id: string) => {
      if (!recordId || !id) {
        throw new Error('Record ID is missing')
      }

      if (selectedItemType === 'task') {
        try {
          await dispatch(deleteTaskThunk({ dealId: recordId, taskId: id, recordType: 'prospect' })).unwrap()
          setTimeout(() => {
            handleDetailClose()
          }, 100)
        } catch (error) {
          console.error('Error in handleDetailDelete:', error)
          setTimeout(() => {
            handleDetailClose()
          }, 100)
          throw error
        }
        return
      }

      const interactionId = id
      try {
        const currentToken = getAccessToken(store.getState().auth)
        await deleteInteraction(recordId, interactionId, currentToken)
        dispatch(removeInteraction(recordId, interactionId))
        const refreshedInteractions = await fetchInteractionsForProspect(recordId, {}, currentToken)
        dispatch(setInteractions(recordId, refreshedInteractions))
        setTimeout(() => {
          handleDetailClose()
        }, 100)
      } catch (error) {
        console.error('Error in handleDetailDelete:', error)
        setTimeout(() => {
          handleDetailClose()
        }, 100)
        throw error
      }
    },
    [recordId, dispatch, handleDetailClose, selectedItemType]
  )

  const handleDetailDelink = useCallback(
    async (interactionId: string) => {
      if (!recordId || !interactionId) {
        throw new Error('Record ID or Interaction ID is missing')
      }

      try {
        const currentToken = getAccessToken(store.getState().auth)
        await delinkInteraction(recordId, interactionId, 'prospect', currentToken)
        dispatch(removeInteraction(recordId, interactionId))
        const refreshedInteractions = await fetchInteractionsForProspect(recordId, {}, currentToken)
        dispatch(setInteractions(recordId, refreshedInteractions))
        setTimeout(() => {
          handleDetailClose()
        }, 100)
      } catch (error) {
        console.error('Error in handleDetailDelink:', error)
        setTimeout(() => {
          handleDetailClose()
        }, 100)
        throw error
      }
    },
    [recordId, dispatch, handleDetailClose]
  )

  const refreshTasksRef = useRef<(() => Promise<void>) | null>(null)

  const handleRefreshTasks = useCallback(async () => {
    if (refreshTasksRef.current) {
      await refreshTasksRef.current()
    }
  }, [])

  const handleDetailSubmit = async (data: any, mode: string) => {
    if (!recordId || recordId === 'new') {
      console.warn('Cannot save task/interaction: Prospect ID is not available')
      return
    }

    try {
      if (mode === 'add-task') {
        const taskData = {
          ...data,
          [TaskRecordFields.task_prospects__prospect_id]: recordId
        }
        const investorValue = prospect?.[ProspectFields.prospects__investor]
        const accountId =
          typeof investorValue === 'object'
            ? investorValue?.value ?? investorValue?.id ?? investorValue?.accounts__id ?? null
            : investorValue ?? null
        const result = await dispatch(
          createTask({
            dealId: recordId,
            taskData,
            recordType: 'prospect',
            accountId
          }) as any
        )

        if (result.payload?.task && prospect) {
          const existingTasks = prospect[ProspectFields.prog__tasks] || []
          const updatedTasks = [result.payload.task, ...existingTasks]

          dispatch(
            updateActiveRecord({
              uuid: recordId,
              updates: {
                [ProspectFields.prog__tasks]: updatedTasks
              }
            })
          )

          dispatch(
            updateProspectRecord({
              prospectId: recordId,
              updates: {
                [ProspectFields.prog__tasks]: updatedTasks
              }
            })
          )
        }
        await handleRefreshTasks()
      } else if (mode === 'edit-task') {
        const taskData = {
          ...data
        }
        const taskId = selectedItem?.[TaskRecordFields.tasks__id]
        // Same guard as the prospect drawer: edit mode needs a selected task with an id.
        if (!taskId) {
          throw new Error('Task ID is missing')
        }

        const result = await dispatch(
          updateTask({
            dealId: recordId,
            taskId,
            taskData,
            recordType: 'prospect'
          }) as any
        )

        if (result.payload?.task && prospect) {
          const existingTasks = prospect[ProspectFields.prog__tasks] || []
          const taskIdFromResult = result.payload.task[TaskRecordFields.tasks__id] || taskId
          const updatedTasks = existingTasks.map((t: any) => {
            const existingTaskId = t?.[TaskRecordFields.tasks__id]
            if (existingTaskId?.toString() === taskIdFromResult?.toString()) {
              return result.payload.task
            }
            return t
          })

          dispatch(
            updateActiveRecord({
              uuid: recordId,
              updates: {
                [ProspectFields.prog__tasks]: updatedTasks
              }
            })
          )

          dispatch(
            updateProspectRecord({
              prospectId: recordId,
              updates: {
                [ProspectFields.prog__tasks]: updatedTasks
              }
            })
          )
        }
        await handleRefreshTasks()
      } else if (mode === 'add-interaction') {
        const interactionData = {
          ...data,
          interaction_prospects__prospect_id: recordId
        }
        const investorValue = prospect?.[ProspectFields.prospects__investor]
        const accountId =
          typeof investorValue === 'object'
            ? investorValue?.value ?? investorValue?.id ?? investorValue?.accounts__id ?? null
            : investorValue ?? null
        const currentToken = getAccessToken(store.getState().auth)
        const createdInteraction = await createInteraction(
          recordId,
          interactionData,
          currentToken,
          'prospect',
          accountId
        )

        dispatch(addInteraction(recordId, createdInteraction))
      } else if (mode === 'edit-interaction') {
        const interactionData = {
          ...data
        }
        const interactionId = selectedItem?.[InteractionRecordFields.interactions__id]
        if (!interactionId) {
          throw new Error('Interaction ID is missing')
        }
        const currentToken = getAccessToken(store.getState().auth)
        const updatedInteraction = await updateInteraction(
          recordId,
          interactionId,
          interactionData,
          currentToken,
          'prospect'
        )

        dispatch(updateInteractionAction(recordId, interactionId, updatedInteraction))
      }

      setDetailMode('view')
    } catch (error) {
      console.error('Failed to save:', error)
      throw error
    }
  }

  const isDetailPanelOpen = useMemo(() => {
    return selectedItem || detailMode !== 'view'
  }, [selectedItem, detailMode])

  const investorId = prospect?.[ProspectFields.prospects__investor] || null
  const refreshInteractionsRef = useRef<(() => Promise<void>) | null>(null)

  const handleTeamTableChange = useCallback(
    async (updatedCompanyTeamMembers: any[]) => {
      if (!recordId) {
        if (onCompanyTeamMembersChange) {
          onCompanyTeamMembersChange(updatedCompanyTeamMembers)
        }
        return
      }

      const currentToken = getAccessToken(store.getState().auth)
      if (!currentToken) {
        if (onCompanyTeamMembersChange) {
          onCompanyTeamMembersChange(updatedCompanyTeamMembers)
        }
        return
      }

      try {
        const response = await fetchFundraisingById(recordId, currentToken)
        if (response.data && !response.error) {
          const prospectData = response.data

          dispatch(
            updateProspectRecord({
              prospectId: recordId.toString(),
              updates: prospectData
            })
          )
        }
      } catch (error) {
        console.error('Error refreshing prospect data after team table change:', error)
        if (onCompanyTeamMembersChange) {
          onCompanyTeamMembersChange(updatedCompanyTeamMembers)
        }
      }
    },
    [recordId, dispatch, onCompanyTeamMembersChange]
  )

  const tasksSectionProps = useMemo(
    () => ({
      recordId: recordId !== 'new' ? recordId : null,
      recordType: 'prospect' as const,
      onTaskSelect: handleTaskSelect,
      selectedTaskId: selectedItemType === 'task' ? selectedItem?.[TaskRecordFields.tasks__id] : null,
      onAddTask: handleAddTask,
      onEditModeChange: handleTaskEditModeChange,
      disableInlineEditing: true,
      onRefreshRequest: (refreshFn: () => Promise<void>) => {
        refreshTasksRef.current = refreshFn
        if (onRefreshTasksReady) {
          onRefreshTasksReady(refreshFn)
        }
      }
    }),
    [
      recordId,
      selectedItemType,
      selectedItem?.[TaskRecordFields.tasks__id],
      handleTaskSelect,
      handleAddTask,
      handleTaskEditModeChange,
      onRefreshTasksReady
    ]
  )

  const interactionsSectionProps = useMemo(
    () => ({
      recordId: recordId !== 'new' ? recordId : null,
      recordType: 'prospect' as const,
      onInteractionSelect: handleInteractionSelect,
      selectedInteractionId:
        selectedItemType === 'interaction' ? selectedItem?.[InteractionRecordFields.interactions__id] : null,
      onAddInteraction: handleAddInteraction,
      onRefreshRequest: (refreshFn: () => Promise<void>) => {
        refreshInteractionsRef.current = refreshFn
        if (onRefreshInteractionsReady) {
          onRefreshInteractionsReady(refreshFn)
        }
      },
      disableInlineEditing: true
    }),
    [
      recordId,
      selectedItemType,
      selectedItem?.[InteractionRecordFields.interactions__id],
      handleInteractionSelect,
      handleAddInteraction,
      onRefreshInteractionsReady
    ]
  )

  return (
    <div
      data-debug='dashboard-tab-container'
      data-component='DashboardTab'
      data-record-id={recordId}
      data-detail-mode={detailMode}
      data-selected-item-type={selectedItemType}
      className='flex-1 flex-col h-full'
      style={{ overflowY: 'auto', overflowX: 'hidden' }}
    >
      {/* Prospect tracker (LP agent programme Phase 4 + 4b): the Current
          State card row leads the page — interest / objections / decision
          process, the dated deadline, open asks: the agent-maintained overlay
          on the human-owned pipeline status. Full history on the Agent tab.
          The open signals left this strip's footer for a section of their own
          below the drafts (owner, 2026-09-06). */}
      {recordId ? (
        <div data-debug='dashboard-tab-prospect-states-row' className='mb-3'>
          <ProspectStatesStrip
            prospectId={String(recordId)}
            onTaskCreated={handleRefreshTasks}
            onRefreshReady={onRefreshProspectStatesReady}
          />
        </div>
      ) : null}

      {/* Agent drafts lead the record (owner, 2026-09-03, order revised
          2026-09-06): the proposed next step sits directly under the Current
          State it answers and ahead of the signals, because it is the one
          thing here that needs a click. Hidden until one exists. */}
      {recordId ? (
        <div data-debug='dashboard-tab-agent-drafts-section' className='mb-3'>
          <AgentDraftsSection
            contextTable='prospects'
            contextId={recordId}
            hideWhenEmpty
            collapseKey='prospect_dashboard_drafts'
          />
        </div>
      ) : null}

      {/* The tracker's open signals — open-only, newest three, never crit-only
          (the states row is the noise control). Its own section since
          2026-09-06, the same shape every other agent surface uses. */}
      {recordId ? (
        <div data-debug='dashboard-tab-prospect-signals-section' className='mb-3'>
          <AgentSignalsSection
            contextTable='prospects'
            contextId={recordId}
            openOnly
            maxVisible={3}
            hideWhenEmpty
            collapseKey='prospect_dashboard_signals'
            agentTypeKeys={['prospect_tracker']}
            onViewMore={onNavigateToAgentTab}
            onTaskCreated={handleRefreshTasks}
          />
        </div>
      ) : null}

      {/* The Prospect Brief (LP agent programme Phase 4) follows the drafts
          at full width — the standing narrative behind the chips and the
          proposed step; hidden until the tracker's first deep run. */}
      {recordId ? (
        <div data-debug='dashboard-tab-prospect-brief-section' className='mb-3'>
          <ProspectBriefCard prospectId={recordId} hideWhenEmpty />
        </div>
      ) : null}

      <Row data-debug='dashboard-tab-main-row' gutter={[16, 16]} className='dashboard-tab-row h-full'>
        <Col span={isDetailPanelOpen ? 6 : 14} className='h-full flex flex-col'>
          <div data-debug='dashboard-tab-left-column' className='flex flex-col h-full space-y-2'>
            <div data-debug='dashboard-tab-overview-section'>
              <ProspectOverviewSection
                prospect={prospect}
                formData={prospect}
                handleFieldChange={handleFieldChange}
                onPatchSuccess={onPatchSuccess}
                selectedContacts={selectedContacts}
                onContactsChange={setSelectedContacts}
                isDrawer={false}
                recordId={recordId}
                cardProps={{ className: 'mb-2' }}
              />
            </div>

            <div data-debug='dashboard-tab-interaction-summary-section'>
              <FormSection
                title={SECTION_INTERACTION_SUMMARY}
                titleColor='blue'
                className='mb-2'
              >
                <InputManager
                  inputMode='read'
                  labelPosition='inline'
                  fields={getInteractionSummaryFields(prospect || {})}
                  fieldWrapping={true}
                />
              </FormSection>
            </div>

            {investorId && (
              <div data-debug='dashboard-tab-investor-team-section' className='flex-shrink-0'>
                <CompanyTeamTable
                  key={companyTeamMembersKey || 'investor-team-empty'}
                  mode='existing'
                  companyId={investorId}
                  initialTeamMembers={companyTeamMembers}
                  onChange={handleTeamTableChange}
                  readOnly={false}
                  title={SECTION_INVESTOR_TEAM}
                />
              </div>
            )}
          </div>
        </Col>

        <Col span={isDetailPanelOpen ? 8 : 10} className='h-full flex flex-col'>
          <div data-debug='dashboard-tab-right-column' className='flex flex-col h-full space-y-2'>
            <div data-debug='dashboard-tab-investor-section'>
              <InvestorSection
                formData={prospect}
                handleFieldChange={handleFieldChange}
                onPatchSuccess={onPatchSuccess}
                recordId={recordId}
                investorId={investorId}
                cardProps={{ className: 'mb-2' }}
              />
            </div>

            <div data-debug='dashboard-tab-tasks-section' className='flex-shrink-0'>
              <TasksSection {...tasksSectionProps} />
            </div>

            <div
              data-debug='dashboard-tab-interactions-section'
              className={`overflow-hidden h-full ${isTaskInEditMode ? 'flex-shrink min-h-0' : 'flex-1 min-h-0'}`}
            >
              <InteractionsSection {...interactionsSectionProps} />
            </div>
          </div>
        </Col>

        {isDetailPanelOpen && (
          <Col span={10} className='h-full'>
            <div data-debug='dashboard-tab-detail-column' className='h-full flex flex-col overflow-hidden'>
              <DetailPanel
                selectedItem={selectedItem}
                itemType={selectedItemType}
                mode={detailMode}
                onClose={handleDetailClose}
                onEdit={handleDetailEdit}
                onSubmit={handleDetailSubmit}
                onDelete={handleDetailDelete}
                onDelink={handleDetailDelink}
                onRefreshInteractions={async () => {
                  if (refreshInteractionsRef.current) {
                    await refreshInteractionsRef.current()
                  }
                }}
                onRefreshTasks={handleRefreshTasks}
              />
            </div>
          </Col>
        )}
      </Row>
    </div>
  )
}
